/**
 * Field-level issues out of a `VALIDATION_FAILED` envelope (FF-AUDIT-003/021).
 *
 * The API's 422 `details` are `{ path: 'a.b', code, message, params? }` — the
 * route hook's shape, which service-level 422s also use (`validationDetails`).
 * A form keys its own localized message on `params.code` (the rule that
 * failed) and falls back to the field's generic copy — never on the server's
 * English `message`, which is a localization defect (repo CLAUDE.md).
 */
export interface FieldIssue {
  /** Dotted path, e.g. `profile.birthDate` or `expiresOn`. */
  path: string
  code?: string
}

export function fieldIssuesFromError(err: unknown): FieldIssue[] {
  const details = (err as { details?: unknown[] } | null)?.details
  if (!Array.isArray(details)) return []
  const out: FieldIssue[] = []
  for (const detail of details) {
    const d = detail as { path?: unknown; code?: unknown; params?: { code?: unknown } }
    const path = Array.isArray(d.path) ? d.path.map(String).join('.') : typeof d.path === 'string' ? d.path : ''
    if (!path) continue
    // `params.code` is the rule a contract refinement named; a form-answer
    // issue (`forms.validation.ts`) puts its rule in `code` itself
    // (`too_small`, `too_big`, `invalid_option`…) — FF-AUDIT-005.
    const code =
      typeof d.params?.code === 'string' ? d.params.code : typeof d.code === 'string' ? d.code : undefined
    out.push({ path, ...(code ? { code } : {}) })
  }
  return out
}

/** The browser's local calendar date as `YYYY-MM-DD` — what a date input shows. */
export function localDateToday(now = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
